import React from 'react';
import Modal from 'react-modal';
import { Form, Formik, FormikHelpers } from 'formik';
import { Oval } from 'react-loader-spinner';
import * as Yup from 'yup';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import {
  uploadDriverDocument,
  listDriverDocuments,
  resetUploadDriverDocument,
} from '../../../DriversV2/driversV2Slice';
import { prepareDriverDocument, FileTooLargeError } from '../../../../lib/prepareDriverDocument';
import { DriverDocumentCategory } from '../../../../models';
import { ReactComponent as Close } from '../../../../assets/images/close.svg';

import './UploadDocumentModal.scss';

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#FFFFFF',
  },
};

const validationSchema = Yup.object().shape({
  category: Yup.string()
    .oneOf(['license', 'medical_card', 'other'])
    .required('Category is required'),
  label: Yup.string()
    .required('Label is required'),
});

interface UploadDocumentFormModel {
  category: DriverDocumentCategory;
  label: string;
}

export interface UploadDocumentModalProps {
  isOpen: boolean;
  driverId: string;
  onClose: () => void;
}

export function UploadDocumentModal({ isOpen, driverId, onClose }: UploadDocumentModalProps) {
  const dispatch = useAppDispatch();

  const uploadAPI = useAppSelector(state => state.driversV2.uploadDriverDocument);
  const isLoading = uploadAPI.loading;
  const error = uploadAPI.error;
  const uploaded = !!uploadAPI.value;

  const [fileError, setFileError] = React.useState<string | null>(null);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);

  // Guard success/error side-effects on a genuine loading: true → false
  // transition (mirrors prevDeleteLoading pattern in DocumentsTab.tsx).
  // Without this, reopening the modal with stale `uploaded=true` would
  // auto-fire toast+refetch+close before the user submits anything.
  const prevUploadLoading = React.useRef(false);
  React.useEffect(() => {
    const wasLoading = prevUploadLoading.current;
    prevUploadLoading.current = isLoading;

    if (!wasLoading) return; // not a real loading → idle transition

    if (!isLoading && !!error) {
      toast.error('Something went wrong. Please try again.');
    }

    if (!isLoading && !error && uploaded) {
      toast.success('Document uploaded.');
      dispatch(resetUploadDriverDocument());
      dispatch(listDriverDocuments(driverId));
      onClose();
    }
  }, [isLoading, error, uploaded]);

  const handleClose = () => {
    dispatch(resetUploadDriverDocument());
    setFileError(null);
    setSelectedFile(null);
    onClose();
  };

  const onSubmit = async (
    values: UploadDocumentFormModel,
    helpers: FormikHelpers<UploadDocumentFormModel>
  ) => {
    setFileError(null);

    if (!selectedFile) {
      setFileError('Please select a file.');
      return;
    }

    let base64: string;
    let contentType: string;

    try {
      const prepared = await prepareDriverDocument(selectedFile);
      base64 = prepared.base64;
      contentType = prepared.contentType;
    } catch (err) {
      if (err instanceof FileTooLargeError) {
        setFileError(err.message);
        return;
      }
      setFileError('Could not read the file. Please try again.');
      return;
    }

    dispatch(uploadDriverDocument({
      driverId,
      req: {
        category: values.category,
        label: values.label,
        fileBase64: base64,
        contentType,
      },
    }));
  };

  const initialValues: UploadDocumentFormModel = {
    category: 'license',
    label: '',
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={modalStyles}
    >
      <div className='upload-document-modal-component'>
        <Close className='close-button' onClick={handleClose} />

        <div className='title'>Upload Document</div>

        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {({ errors, touched, setFieldValue }) => (
            <Form className='upload-document-form'>

              <div className='field-container'>
                <div className='label'>Category:</div>
                <select
                  name='category'
                  className='text-input'
                  defaultValue='license'
                  onChange={e => setFieldValue('category', e.target.value)}
                >
                  <option value='license'>License</option>
                  <option value='medical_card'>Medical card</option>
                  <option value='other'>Other</option>
                </select>
                {errors.category && touched.category && (
                  <div className='form-error'>{errors.category}</div>
                )}
              </div>

              <div className='field-container'>
                <div className='label'>Label:</div>
                <input
                  name='label'
                  className='text-input'
                  placeholder='e.g. CDL Class A'
                  onChange={e => setFieldValue('label', e.target.value)}
                />
                {errors.label && touched.label && (
                  <div className='form-error'>{errors.label}</div>
                )}
              </div>

              <div className='field-container'>
                <div className='label'>File:</div>
                <input
                  type='file'
                  accept='image/*,application/pdf'
                  onChange={e => {
                    setFileError(null);
                    setSelectedFile(e.target.files?.[0] ?? null);
                  }}
                />
                {fileError && (
                  <div className='form-error file-error' data-testid='file-error'>
                    {fileError}
                  </div>
                )}
              </div>

              <button
                type='submit'
                className='upload-document-button'
                disabled={isLoading}
              >
                Upload
                {isLoading && (
                  <div style={{ marginLeft: 8 }}>
                    <Oval color='#131313' height={20} width={20} secondaryColor='#FBD20A' />
                  </div>
                )}
              </button>

              {!!error && (
                <div className='error-text'>
                  Something went wrong. Please try again.
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}
